import {gql} from '@apollo/client';

/**
 * Creates a new palowan-level vacation assignment override for a given seniority range.
 * The palowan's country is derived automatically from the USER record on the backend.
 *
 * @param palowanId - The ID of the palowan to assign vacation days to.
 * @param seniorityFromYears - Inclusive start of the seniority range in years.
 * @param seniorityToYears - Inclusive end of the seniority range in years.
 * @param vacationDays - Number of vacation days to assign for this range.
 */
export const CREATE_PALOWAN_VACATION_ASSIGNMENT = gql`
  mutation CreatePalowanVacationAssignment(
    $palowanId: ID!
    $seniorityFromYears: Int!
    $seniorityToYears: Int!
    $vacationDays: Int!
  ) {
    createPalowanVacationAssignment(
      palowanId: $palowanId
      seniorityFromYears: $seniorityFromYears
      seniorityToYears: $seniorityToYears
      vacationDays: $vacationDays
    ) {
      id
      palowan {
        id
        firstName
        lastName
        country
      }
      seniorityFromYears
      seniorityToYears
      vacationDays
      isActive
    }
  }
`;

/**
 * Disables (soft-deletes) an existing palowan-level vacation assignment.
 * Sets the record's isActive field to false. The record remains visible in
 * the admin table with an inactive visual indicator.
 *
 * @param id - UUID of the palowan vacation assignment to disable.
 */
export const DISABLE_PALOWAN_VACATION_ASSIGNMENT = gql`
  mutation DisablePalowanVacationAssignment($id: String!) {
    disablePalowanVacationAssignment(id: $id) {
      id
      palowan {
        id
        firstName
        lastName
        country
      }
      seniorityFromYears
      seniorityToYears
      vacationDays
      isActive
    }
  }
`;

/**
 * Enables (soft-restores) an existing palowan-level vacation assignment.
 * Sets the record's isActive field to true. The record becomes active again
 * in the admin table, reverting to the standard active visual style.
 *
 * @param id - UUID of the palowan vacation assignment to enable.
 */
export const ENABLE_PALOWAN_VACATION_ASSIGNMENT = gql`
  mutation EnablePalowanVacationAssignment($id: String!) {
    enablePalowanVacationAssignment(id: $id) {
      id
      palowan {
        id
        firstName
        lastName
        country
      }
      seniorityFromYears
      seniorityToYears
      vacationDays
      isActive
    }
  }
`;

/**
 * Updates the seniority range and vacation days of an existing palowan vacation assignment.
 * Only the numeric fields are mutable; palowan cannot be changed.
 *
 * @param id - UUID of the palowan vacation assignment to update.
 * @param seniorityFromYears - New inclusive start of the seniority range in years.
 * @param seniorityToYears - New inclusive end of the seniority range in years.
 * @param vacationDays - New number of vacation days for this range.
 */
export const UPDATE_PALOWAN_VACATION_ASSIGNMENT = gql`
  mutation UpdatePalowanVacationAssignment(
    $id: String!
    $seniorityFromYears: Int!
    $seniorityToYears: Int!
    $vacationDays: Int!
  ) {
    updatePalowanVacationAssignment(
      id: $id
      seniorityFromYears: $seniorityFromYears
      seniorityToYears: $seniorityToYears
      vacationDays: $vacationDays
    ) {
      id
      palowan {
        id
        firstName
        lastName
        country
      }
      seniorityFromYears
      seniorityToYears
      vacationDays
      isActive
      updatedAt
    }
  }
`;
